import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { useSnackbar } from '@stores/snackbar';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import useScrollHandlerGroup from '@hooks/useScrollHandlerGroup';
import { scrollConstants } from '@utils/constants';
import useFollowedGoals from '@api/hooks/goal/useFollowedGoals';
import { followGoal } from '@api/fetchers/goal/updaters';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { segment } from '@actions/analytics';
import ApplicationProcess from './ApplicationProcess';
import CaseStudies from './CaseStudies';
import Curriculum from './Curriculum';
import Navigation from './Navigation';
import Overview from './Overview';
import StickyInfoCard from './StickyInfoCard';
import CertificationCoursesTTU from './CertificationCoursesTTU';
import CertificationCoursesFAQS from './CertificationCoursesFAQS';
import CourseFees from './CourseFees';
import Certification from './Certification';
import SuccessStories from './SuccessStories';
import useScrollInfo from './useScrollInfo';
import getRefsMap, { getProgramLandingPage } from './utils';
import {
  TABS_CONSTANT,
  CONTAIN_PADDING,
  EVENT_NAME_MAPPING
} from './constants';
import useCertificationCourseData from './useCertificationCourseData';
import CourseBanner from './Banner';
import Faculty from './Faculty';
import Form from './Form';

const Container = styled.div`
  background: var(--color-i-white);
  width: 100%;
`;
const HeaderContainer = styled.div`
  width: 100%;
  border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
`;

const HeaderWrapper = styled.div`
  width: 100%;
`;

const Content = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  display: grid;
  margin-top: ${Spacings.SPACING_14B};
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: auto;
    margin-top: ${Spacings.SPACING_7B};
  }
`;

const LeftContainer = styled.div`
  grid-column: span 16;
  width: 768px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const RightContainer = styled.div`
  grid-column: 17/25;
  margin-bottom: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Section = styled.div`
  margin: ${({ $isFirstTab }) => {
    return $isFirstTab
      ? `${Spacings.SPACING_0B}`
      : `${Spacings.SPACING_14B} ${Spacings.SPACING_0B}`;
  }};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${({ $isFirstTab }) => {
      return $isFirstTab
        ? `${Spacings.SPACING_0B}`
        : `${Spacings.SPACING_12B} ${Spacings.SPACING_0B}`;
    }};
    padding: ${({ $hasPadding }) => {
      return $hasPadding
        ? `${Spacings.SPACING_0B} ${Spacings.SPACING_4B}`
        : `${Spacings.SPACING_0B}`;
    }};
  }
`;

const CourseContainer = ({ goalUID, goalInfo }) => {
  const overViewRef = createRef();
  const curriculumRef = createRef();
  const caseStudiesRef = createRef();
  const facultyRef = createRef();
  const faqsRef = createRef();
  const certificationRef = createRef();
  const successStoriesRef = createRef();
  const courseFeesRef = createRef();
  const applicationProcessRef = createRef();
  const { availableSections } = useScrollInfo(goalUID);
  const [selectedTab, setSelectedTab] = useState(0);
  const [clickedCTA, setClickedCTA] = useState(null);
  const { open: showSnackbar } = useSnackbar();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const {
    followedGoals,
    isValidating: areFollowedGoalsValidating,
    mutate: revalidateFollwedGoals
  } = useFollowedGoals();

  const { overAll = {} } = useCertificationCourseData(goalUID);
  const { isBrochureProtected, formQuestionsPresent } = overAll;
  const [isRequestingDownloadBrochure, setIsRequestingDownloadBrochure] =
    useState(false);

  const REFS = useMemo(
    () =>
      getRefsMap({
        applicationProcessRef,
        caseStudiesRef,
        certificationRef,
        courseFeesRef,
        curriculumRef,
        facultyRef,
        faqsRef,
        overViewRef,
        successStoriesRef
      }),
    [
      applicationProcessRef,
      caseStudiesRef,
      certificationRef,
      courseFeesRef,
      curriculumRef,
      facultyRef,
      faqsRef,
      overViewRef,
      successStoriesRef
    ]
  );

  const refs = useMemo(
    () =>
      availableSections?.map((section) => ({
        ref: REFS[section],
        shrinkPos: scrollConstants.scroll100,
        id: section
      })),
    [REFS, availableSections]
  );

  const offsetTop = scrollConstants.scroll100;
  const { activeStatuses, scrollToElement } = useScrollHandlerGroup(refs, {
    offsetTop
  });

  const activeSection = activeStatuses.indexOf(true);

  const getSelectedTab = useCallback(() => {
    if (activeSection === selectedTab) return null;
    if (activeSection < 0) return null;
    setSelectedTab(activeSection);
  }, [activeSection, selectedTab]);

  useEffect(() => {
    getSelectedTab();
    if (isLoggedIn) revalidateFollwedGoals();
  }, [getSelectedTab, isLoggedIn, revalidateFollwedGoals]);

  async function handleInterestedGoal() {
    /**
     * If the current goal is not yet followed by the user
     * pin the goal for the user.
     */
    const shouldPinGoal =
      !followedGoals.find(
        ({ uid: followedGoalUID }) => followedGoalUID === goalUID
      ) && !areFollowedGoalsValidating;
    if (shouldPinGoal) {
      await followGoal(goalUID, true);
      // Once the goal is pinned, revalidate the followed goals
      revalidateFollwedGoals();
    }
  }

  const scrollToRef = (ref, id) => {
    scrollToElement(ref.current, id);
  };

  const handleChange = (tabIndex) => {
    const section = availableSections[tabIndex];
    setSelectedTab(tabIndex);
    scrollToRef(REFS[section], section);
  };

  const handleSendEvent = (eventName, extraProps = {}) => {
    segment.track(eventName, {
      form_details: [],
      goal_uid: goalInfo?.uid,
      goal_name: goalInfo?.name,
      ...extraProps
    });
  };

  const handleIInterestedCTA = () => {
    setClickedCTA(EVENT_NAME_MAPPING.I_AM_INTERESTED);
    if (!isLoggedIn) {
      return openLoginDrawer();
    }
    const isFormSubmitted = getProgramLandingPage().includes(goalUID);
    if (!formQuestionsPresent || isFormSubmitted) {
      handleSendEvent(EVENT_NAME_MAPPING.I_AM_INTERESTED);
      showSnackbar({
        type: 'info',
        message:
          'Your details are successfully submitted. Our academic team will get back to you shortly',
        time: 3000
      });
    }
    if (!formQuestionsPresent && isLoggedIn) {
      handleInterestedGoal();
    }
  };

  const handleDownloadBrochureCTA = () => {
    setClickedCTA(EVENT_NAME_MAPPING.DOWNLOAD_BROCHURE);
    if (isBrochureProtected && !isLoggedIn) {
      setIsRequestingDownloadBrochure(true);
      return openLoginDrawer();
    }
    window.open(overAll?.brochureLinks, '_blank');
    setIsRequestingDownloadBrochure(false);
    const isFormSubmitted = getProgramLandingPage().includes(goalUID);
    if (!formQuestionsPresent || isFormSubmitted || !isBrochureProtected) {
      handleSendEvent(EVENT_NAME_MAPPING.DOWNLOAD_BROCHURE);
    }
    if (!formQuestionsPresent && isLoggedIn) {
      handleInterestedGoal();
    }
  };

  useEffect(() => {
    if (isLoggedIn && isRequestingDownloadBrochure) handleDownloadBrochureCTA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <Container>
      <HeaderContainer>
        <HeaderWrapper>
          <CourseBanner
            goalUID={goalUID}
            handleInterestedCTA={handleIInterestedCTA}
            handleDownloadBrochureCTA={handleDownloadBrochureCTA}
          />
        </HeaderWrapper>
      </HeaderContainer>
      <Navigation
        goalUID={goalUID}
        selectedTab={selectedTab}
        setSelectedTab={handleChange}
      />
      <Content>
        <LeftContainer>
          {availableSections?.map((section, index) => {
            return (
              <Section
                id={section}
                key={section}
                ref={REFS[section]}
                $hasPadding={!CONTAIN_PADDING.includes(section)}
                $isFirstTab={index === 0}
              >
                <Sections
                  type={section}
                  goalUID={goalUID}
                  handleIInterestedCTA={handleIInterestedCTA}
                  handleDownloadBrochureCTA={handleDownloadBrochureCTA}
                />
              </Section>
            );
          })}
          <Section $hasPadding>
            <CertificationCoursesTTU goalUID={goalUID} />
          </Section>
        </LeftContainer>
        <RightContainer>
          <StickyInfoCard
            goalUID={goalUID}
            handleIInterestedCTA={handleIInterestedCTA}
            handleDownloadBrochureCTA={handleDownloadBrochureCTA}
          />
        </RightContainer>
      </Content>
      {formQuestionsPresent && (
        <Form
          goalUID={goalUID}
          clickedCTA={clickedCTA}
          setClickedCTA={setClickedCTA}
          goalInfo={goalInfo}
          handleSendEvent={handleSendEvent}
          // eslint-disable-next-line react/jsx-no-bind
          handleInterestedGoal={handleInterestedGoal}
        />
      )}
    </Container>
  );
};

export default CourseContainer;

const Sections = ({
  type,
  goalUID,
  handleIInterestedCTA,
  handleDownloadBrochureCTA
}) => {
  switch (type) {
    case TABS_CONSTANT.OVERVIEW:
      return <Overview goalUID={goalUID} />;
    case TABS_CONSTANT.CURRICULUM:
      return (
        <Curriculum
          goalUID={goalUID}
          handleDownloadBrochureCTA={handleDownloadBrochureCTA}
        />
      );
    case TABS_CONSTANT.CASE_STUDIES:
      return <CaseStudies goalUID={goalUID} />;
    case TABS_CONSTANT.FACULTIES:
      return <Faculty goalUID={goalUID} />;
    case TABS_CONSTANT.CERTIFICATION:
      return <Certification goalUID={goalUID} />;
    case TABS_CONSTANT.SUCCESS_STORIES:
      return <SuccessStories goalUID={goalUID} />;
    case TABS_CONSTANT.COURSE_FEE:
      return <CourseFees goalUID={goalUID} />;
    case TABS_CONSTANT.APPLICATION_PROCESS:
      return (
        <ApplicationProcess
          goalUID={goalUID}
          handleIInterestedCTA={handleIInterestedCTA}
        />
      );
    case TABS_CONSTANT.FAQs:
      return <CertificationCoursesFAQS goalUID={goalUID} />;
    default:
      return null;
  }
};
